import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormInput from 'components-desktop/FormInput';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class ChangePasswordPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      confirmDirty: false,
      isChangeButtonEnable: true,
    };
  }

  getTitle() {
    return 'Change Password';
  }

  onRepeatPasswordBlur(e) {
    const { value } = e.target;

    this.setState((prevState, props) => ({
      confirmDirty: prevState.confirmDirty || !!value,
    }));
  }

  compareToFirstPassword(rule, value, callback) {
    const { form } = this.props;

    if (value && value !== form.getFieldValue('newPassword')) {
      callback('Please type the same new password again.');
    } else {
      callback();
    }
  }

  validateToNextPassword(rule, value, callback) {
    const { form } = this.props;

    if (value && this.state.confirmDirty) {
      form.validateFields(['repeatPassword'], { force: true });
    }
    callback();
  }

  async changePassword(values) {
    try {
      this.setState({
        isChangeButtonEnable: false,
      });

      const params = {
        userId: this.getMyUserId(),
        oldPassword: helpers.extraFormInputValue(values, 'oldPassword'),
        newPassword: helpers.extraFormInputValue(values, 'newPassword'),
      };
      await this.callService('/user/change_password', params);

      this.notifySuccess('Success', 'Your password has been successfully changed.');

      // Clear inputs.
      this.props.form.setFieldsValue({
        oldPassword: '',
        newPassword: '',
        repeatPassword: '',
      });
    } finally {
      this.setState({
        isChangeButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.changePassword(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const { isChangeButtonEnable } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="blue_madison"
          title="Change Password"
          icon="lock"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="oldPassword"
                    label="Current Password"
                    placeholder="Enter your current password"
                    max={120}
                    isRequired
                    isPassword
                    prefix={<Icon type="lock" style={{ color: 'rgba(0, 0, 0, .25)' }} />}
                  />
                </Col>
              </Row>

              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="newPassword"
                    label="New Password"
                    placeholder="Enter a new password"
                    min={8}
                    max={120}
                    validator={this.validateToNextPassword}
                    isRequired
                    isPassword
                    prefix={<Icon type="lock" style={{ color: 'rgba(0, 0, 0, .25)' }} />}
                  />
                </Col>
              </Row>

              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="repeatPassword"
                    label="Repeat New Password"
                    placeholder="Repeat your new password"
                    validator={this.compareToFirstPassword}
                    isRequired
                    isPassword
                    prefix={<Icon type="lock" style={{ color: 'rgba(0, 0, 0, .25)' }} />}
                    onPressEnter={this.handleSubmit}
                    onBlur={this.onRepeatPasswordBlur}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="blue_madison"
                fontSize="14px"
                type="primary"
                loading={!isChangeButtonEnable}
                htmlType="submit"
              >
                CHANGE PASSWORD
                <Icon type="arrow-right" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
ChangePasswordPage.propTypes = propTypes;

export default Form.create()(ChangePasswordPage);
